/**
 * Marquee — an endless horizontal loop, shared by the two bands of the
 * homepage: the benefits carousel (sections/avantages-carousel.liquid) and the
 * brand banderole (sections/banderole-marques.liquid).
 *
 * Expected markup:
 *
 *   <div data-marquee data-marquee-speed="40">   <- the clipping viewport
 *     <div data-marquee-track>                   <- what moves
 *       <div data-marquee-set> …items… </div>    <- one pass of the content
 *     </div>
 *   </div>
 *
 * How the seam is avoided — same technique as the announcement bar's, lifted
 * out of it and generalised (see sections/announcement-bar.liquid):
 *
 *   · the set is cloned until the copies cover the viewport at least once;
 *   · the whole thing is then DOUBLED, so the track holds 2 x perSet sets;
 *   · translating the track by exactly -50% therefore lands on the copy of
 *     the first set, pixel for pixel, and the animation can restart without
 *     anything visibly jumping.
 *
 * The gutter between two items must live INSIDE each item (a trailing padding
 * or margin), never as a `gap` on the track. A track gap is laid between sets
 * too but not after the last one, so the track would be half a gutter short of
 * twice a set — and -50% would fall half a gutter off the copy, which reads as
 * a stutter once per loop.
 *
 * Under prefers-reduced-motion nothing is cloned and no class is added: the
 * single set stays in the DOM, and each section's CSS lays it out as a static,
 * centred, wrapping row.
 */
(function () {
  var DEFAULT_SPEED = 40; // px per second — "défile lentement"

  function setup(viewport) {
    var track = viewport.querySelector('[data-marquee-track]');
    var set = viewport.querySelector('[data-marquee-set]');
    if (!track || !set) return;

    // Back to the single-set state before measuring: a previous run's clones
    // would otherwise be counted as part of the set.
    track.querySelectorAll('[data-marquee-clone]').forEach(function (node) {
      node.remove();
    });
    track.classList.remove('is-marquee-running');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Les logos arrivent apres le balisage, et la bande se mesure sur EUX :
    // chaque cellule est aussi large que son image, dont la hauteur est un
    // pourcentage de la bande et la largeur `auto`. Tant qu'une image n'est
    // pas chargee, le navigateur ne connait pas son rapport, la cellule sort
    // a zero, et la mesure ci-dessous est fausse — trop de copies, une duree
    // absurde, ou pas de boucle du tout. C'est ce qu'on voyait a l'ouverture
    // de la page : la banderole restait figee jusqu'au premier redimensionnement.
    //
    // Ni DOMContentLoaded ni document.fonts.ready n'attendent les images, et
    // celles-ci sont en `loading="lazy"` : elles se posent quand le lecteur
    // arrive sur la bande, donc bien apres. On remesure a ce moment-la.
    var attente = [];
    set.querySelectorAll('img').forEach(function (img) {
      // `naturalWidth` et non `complete` : c'est le rapport de l'image qui
      // manque a la mesure, et il est connu des qu'elle est decodee. Certains
      // navigateurs laissent `complete` a faux plus longtemps que cela, ce qui
      // aurait fait attendre un evenement `load` deja passe.
      if (!img.naturalWidth) attente.push(img);
    });

    if (attente.length) {
      var relance = function () {
        clearTimeout(viewport.__marqueeTimer);
        // Groupees : six logos qui se posent coup sur coup ne doivent
        // reconstruire la bande qu'une fois.
        viewport.__marqueeTimer = setTimeout(function () {
          setup(viewport);
        }, 60);
      };
      attente.forEach(function (img) {
        img.addEventListener('load', relance, { once: true });
        img.addEventListener('error', relance, { once: true });
      });
    }

    var setWidth = set.getBoundingClientRect().width;
    var available = viewport.clientWidth;
    if (!setWidth || !available) return;

    var perSet = Math.max(1, Math.ceil(available / setWidth));
    var total = perSet * 2; // two identical halves -> seamless -50% loop

    for (var i = 1; i < total; i++) {
      var copy = set.cloneNode(true);
      copy.setAttribute('data-marquee-clone', '');
      // Read once by screen readers, not once per copy.
      copy.setAttribute('aria-hidden', 'true');
      // Clones must not answer to the editor as if they were the real blocks.
      copy.querySelectorAll('[data-shopify-editor-block]').forEach(function (n) {
        n.removeAttribute('data-shopify-editor-block');
      });
      track.appendChild(copy);
    }

    // Constant speed whatever the content weighs: one loop travels exactly
    // half the track, i.e. perSet sets.
    var speed = parseFloat(viewport.dataset.marqueeSpeed) || DEFAULT_SPEED;
    track.style.setProperty('--marquee-duration', (perSet * setWidth) / speed + 's');
    track.classList.add('is-marquee-running');
  }

  function init() {
    document.querySelectorAll('[data-marquee]').forEach(setup);
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(init, 200);
  });

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Web fonts change how wide the text is: measure again once they land.
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(init);

  // Theme editor: a section that reloads must rebuild its loop.
  document.addEventListener('shopify:section:load', init);
})();
